import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { SaveSecretBody, WorkspaceBody } from '@lemoncloud/codes-goods-api';
import { ChevronRight, X } from 'lucide-react';

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    Input,
    Label,
    SharedSpinner,
} from '@codes/ui-kit';
import { useEnrollAccessKey } from '@codes/workspace';

interface AccessKeyFormData {
    accessKey: string;
    secretKey: string;
}

interface AccessKeyDialogProps {
    field: ControllerRenderProps<WorkspaceBody, 'secretId'>;
    onSecretSubmit: (secretId: string) => void;
    disabled?: boolean;
}

export const AccessKeyDialog = ({ field, onSecretSubmit, disabled = false }: AccessKeyDialogProps) => {
    const { t } = useTranslation();

    const secretForm = useForm<SaveSecretBody>({
        defaultValues: {
            accessKey: '',
            secretKey: '',
        },
    });

    const onSubmit = async (secretBody: SaveSecretBody) => {
        await enrollAccessKey(secretBody);
    };

    const onClose = () => {
        if (!workspaceWithSecret || !isSuccess) {
            return;
        }

        secretForm.reset();
        resetQuery();
        onSecretSubmit(workspaceWithSecret.$secret?.id as string);
    };

    const {
        mutateAsync: enrollAccessKey,
        isPending: isEnrolling,
        data: workspaceWithSecret,
        isSuccess,
        reset: resetQuery,
    } = useEnrollAccessKey();

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    disabled={disabled}
                    type="button"
                    variant="outline"
                    className="border-border w-full flex justify-between pr-[10px] disabled:bg-default disabled:border-border disabled:text-text-400"
                >
                    {field.value ? (
                        <div>{field.value}</div>
                    ) : (
                        <>
                            <div className="text-text-400">Please enter your Access key.</div>
                            <ChevronRight className="w-4 h-4 text-sub" />
                        </>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent
                className="py-0 overflow-hidden"
                showCloseButton={false}
                onInteractOutside={e => e.preventDefault()}
                onEscapeKeyDown={e => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('workspace.createV2.accessKey.title')}</DialogTitle>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button className="absolute right-6 top-[19px] z-[51]" type="button">
                                <X className="w-5 h-5" />
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogTitle>{t('workspace.createV2.accessKey.confirmStopEntering')}</AlertDialogTitle>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('workspace.createV2.accessKey.goBack')}</AlertDialogCancel>
                                <AlertDialogCancel className="p-0 flex bg-primary text-white hover:bg-primary">
                                    <DialogClose className="flex-1 flex h-full w-full items-center justify-center">
                                        {t('workspace.createV2.accessKey.stop')}
                                    </DialogClose>
                                </AlertDialogCancel>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DialogHeader>
                <form
                    id="secret-form"
                    onSubmit={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        secretForm.handleSubmit(onSubmit)(e);
                    }}
                >
                    <div className="pt-5 pb-10 overflow-auto">
                        <div className="text-lg font-medium">{t('workspace.createV2.accessKey.title')}</div>
                        <div className="mt-8 space-y-4">
                            <div>
                                <Label variant="form">{t('workspace.createV2.accessKey.accessKeyLabel')}</Label>
                                <Controller
                                    name="accessKey"
                                    control={secretForm.control}
                                    rules={{
                                        required: t('workspace.createV2.accessKey.accessKeyRequired'),
                                    }}
                                    render={({ field, fieldState }) => (
                                        <div>
                                            <Input
                                                {...field}
                                                placeholder={t('workspace.createV2.accessKey.accessKeyPlaceholder')}
                                                className="flex-1"
                                            />
                                            {fieldState.error && (
                                                <div className="text-error text-xs font-medium pl-[6px] mt-1">
                                                    {fieldState.error.message}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                            <div>
                                <Label variant="form">{t('workspace.createV2.accessKey.secretKeyLabel')}</Label>
                                <Controller
                                    name="secretKey"
                                    control={secretForm.control}
                                    rules={{
                                        required: t('workspace.createV2.accessKey.secretKeyRequired'),
                                    }}
                                    render={({ field, fieldState }) => (
                                        <div>
                                            <Input
                                                {...field}
                                                placeholder={t('workspace.createV2.accessKey.secretKeyPlaceholder')}
                                                className="flex-1"
                                            />
                                            {fieldState.error && (
                                                <div className="text-error text-xs font-medium pl-[6px] mt-1">
                                                    {fieldState.error.message}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                            {isSuccess && (
                                <div>
                                    <Label variant="form">Account ID</Label>

                                    <Input
                                        placeholder={t('workspace.createV2.accessKey.accessKeyPlaceholder')}
                                        className="flex-1"
                                        readOnly
                                        value={workspaceWithSecret.$secret?.accountNo}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter className="px-6">
                        {isSuccess ? (
                            <DialogClose asChild onClick={onClose}>
                                <Button size="lg" className="w-full" type="button">
                                    {t('workspace.createV2.accessKey.confirmButton')}
                                </Button>
                            </DialogClose>
                        ) : (
                            <Button
                                size="lg"
                                className="w-full"
                                type="submit"
                                form="secret-form"
                                disabled={isEnrolling}
                            >
                                {isEnrolling ? <SharedSpinner /> : t('workspace.createV2.accessKey.confirmButton')}
                            </Button>
                        )}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
